import {
  AskStrikeUpButton,
  Badge,
  Box,
  Button,
  Card,
  Field,
  Flex,
  Heading,
  HStack,
  Input,
  RefreshButton,
  Stack,
  Text,
  VStack,
} from "components/ui";
import { SetupGuideLink } from "components/common/SetupGuideLink";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdAdd,
  MdCheck,
  MdClose,
  MdDelete,
  MdEdit,
  MdPause,
  MdPlayArrow,
  MdWifi,
} from "react-icons/md";
import {
  NOTIFICATION_PROVIDERS,
  notificationService,
  NotificationChannel,
  NotificationProvider,
} from "services/notificationService";
import {
  ChannelFormState,
  EMPTY_CHANNEL_FORM,
  SlackFields,
  SmtpFields,
  TelegramFields,
  buildChannelSettings,
  channelFormFrom,
  defaultChannelName,
  slackChannelStatus,
  validateChannelForm,
} from "views/admin/customer-companies/components/IntegrationChannelForms";
import {
  TeamsFields,
  teamsChannelStatus,
} from "views/admin/customer-companies/components/TeamsChannelFields";
import { InboundSetupSection } from "views/admin/customer-companies/components/ChatAppSettings";
import {
  RecipientPolicyFields,
  RecipientPolicyState,
  recipientPolicyFrom,
  recipientPolicyRequest,
  recipientPolicySummary,
  validateRecipientPolicy,
} from "./components/RecipientPolicyFields";

const STATUS_COLOR: Record<string, string> = {
  Active: "green",
  Disconnected: "gray",
  Error: "red",
};

const PROVIDER_FIELDS = {
  Smtp: SmtpFields,
  Telegram: TelegramFields,
  Slack: SlackFields,
  Teams: TeamsFields,
};

/** The provider being added, or the channel being edited — one editor at a time. */
interface EditorTarget {
  provider: NotificationProvider;
  /** Null when adding — the channel does not exist yet. */
  channel: NotificationChannel | null;
}

const Notifications = () => {
  const { t } = useTranslation();
  const [channels, setChannels] = useState<NotificationChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Create / edit form
  const [editor, setEditor] = useState<EditorTarget | null>(null);
  const [form, setForm] = useState<ChannelFormState>(EMPTY_CHANNEL_FORM);
  const [recipients, setRecipients] = useState<RecipientPolicyState>(
    recipientPolicyFrom(null),
  );
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  // Test
  const [testingId, setTestingId] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<
    Record<string, { success: boolean; error?: string }>
  >({});

  // Toggle active
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // Delete
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    loadChannels();
  }, []);

  const loadChannels = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await notificationService.listChannels();
      setChannels(data);
    } catch (err: any) {
      setError(err.response?.data?.error || t("app.notifications.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const providerLabel = (provider: NotificationProvider) =>
    t(`app.customerCompanies.integrations.provider.${provider.toLowerCase()}`, {
      defaultValue: provider,
    });

  const openAdd = (provider: NotificationProvider) => {
    setForm({ ...EMPTY_CHANNEL_FORM, name: defaultChannelName(provider) });
    setRecipients(recipientPolicyFrom(null));
    setFormError("");
    setEditor({ provider, channel: null });
  };

  const openEdit = (channel: NotificationChannel) => {
    setForm(channelFormFrom(channel));
    setRecipients(recipientPolicyFrom(channel));
    setFormError("");
    setEditor({ provider: channel.providerType, channel });
  };

  const patchForm = (patch: Partial<ChannelFormState>) =>
    setForm((prev) => ({ ...prev, ...patch }));

  const handleSave = async () => {
    if (!editor) return;
    const isSmtp = editor.provider === "Smtp";
    const invalid =
      validateChannelForm(
        form,
        editor.provider,
        editor.channel !== null,
        t,
        editor.channel ? channelFormFrom(editor.channel) : null,
      ) || (isSmtp ? validateRecipientPolicy(recipients, t) : null);
    if (invalid) {
      setFormError(invalid);
      return;
    }
    try {
      setSaving(true);
      setFormError("");
      const settings = {
        ...buildChannelSettings(form, editor.provider),
        ...(isSmtp ? recipientPolicyRequest(recipients) : {}),
      };
      const saved = editor.channel
        ? await notificationService.updateChannel(editor.channel.id, {
            name: form.name.trim(),
            ...settings,
          })
        : await notificationService.createChannel({
            name: form.name.trim(),
            providerType: editor.provider,
            ...settings,
          });
      setChannels((prev) =>
        prev.some((c) => c.id === saved.id)
          ? prev.map((c) => (c.id === saved.id ? saved : c))
          : [...prev, saved],
      );
      // A new Slack channel without a token goes straight on to its manifest.
      if (
        !editor.channel &&
        saved.providerType === "Slack" &&
        !saved.slackSettings?.hasBotToken
      ) {
        openEdit(saved);
      } else {
        setEditor(null);
      }
    } catch (err: any) {
      setFormError(
        err.response?.data?.error ||
          t(
            editor.channel
              ? "app.notifications.channel.saveFailed"
              : "app.notifications.channel.createFailed",
          ),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (id: string) => {
    try {
      setTestingId(id);
      const result = await notificationService.testConnection(id);
      setTestResults((prev) => ({ ...prev, [id]: result }));
      if (result.success) {
        setChannels((prev) =>
          prev.map((c) => (c.id === id ? { ...c, status: "Active" } : c)),
        );
      }
    } catch (err: any) {
      setTestResults((prev) => ({
        ...prev,
        [id]: {
          success: false,
          error:
            err.response?.data?.error ||
            t("app.notifications.channel.testFailed"),
        },
      }));
    } finally {
      setTestingId(null);
    }
  };

  const handleToggleActive = async (id: string) => {
    try {
      setTogglingId(id);
      const updated = await notificationService.toggleActive(id);
      setChannels((prev) => prev.map((c) => (c.id === id ? updated : c)));
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          t("app.notifications.channel.toggleFailed"),
      );
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t("app.notifications.channel.deleteConfirm"))) return;
    try {
      setDeletingId(id);
      await notificationService.deleteChannel(id);
      setChannels((prev) => prev.filter((c) => c.id !== id));
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          t("app.notifications.channel.deleteFailed"),
      );
    } finally {
      setDeletingId(null);
    }
  };

  const missingProviders = NOTIFICATION_PROVIDERS.filter(
    (provider) => !channels.some((c) => c.providerType === provider),
  );

  const tokenStatus = (hasBotToken: boolean) =>
    hasBotToken
      ? t("app.customerCompanies.integrations.tokenStored", {
          defaultValue: "Token stored",
        })
      : t("app.notifications.channel.noToken");

  const renderEditor = (target: EditorTarget) => {
    const ProviderFields = PROVIDER_FIELDS[target.provider];
    return (
      <Stack gap={3}>
        <Flex justifyContent="space-between" alignItems="center" gap={3}>
          <Heading size="sm">
            {target.channel
              ? providerLabel(target.provider)
              : t("app.notifications.channel.newChannel", {
                  provider: providerLabel(target.provider),
                })}
          </Heading>
          <SetupGuideLink
            doc="notification-channels-setup"
            section={target.provider.toLowerCase()}
            intent="ghost"
            label={t("app.common.whereDoIFindThese")}
          />
        </Flex>
        {target.channel && (
          <InboundSetupSection
            channel={target.channel}
            onDownloadTeamsPackage={() =>
              notificationService.downloadTeamsPackage(target.channel!.id)
            }
          />
        )}
        <Field label={t("app.notifications.channel.fields.name")} required>
          <Input
            value={form.name}
            onChange={(e) => patchForm({ name: e.target.value })}
          />
        </Field>
        <ProviderFields
          form={form}
          onChange={patchForm}
          existing={target.channel !== null}
          slackSettings={target.channel?.slackSettings}
          t={t}
        />
        {target.provider === "Smtp" && (
          <RecipientPolicyFields
            value={recipients}
            onChange={setRecipients}
            t={t}
          />
        )}
        {formError && (
          <Text color="red.400" fontSize="sm">
            {formError}
          </Text>
        )}
        <HStack gap={2}>
          <Button
            colorPalette="brand"
            size="sm"
            onClick={handleSave}
            loading={saving}
          >
            <MdCheck />
            {t("app.common.save")}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setEditor(null)}>
            <MdClose />
            {t("app.common.cancel")}
          </Button>
        </HStack>
      </Stack>
    );
  };

  return (
    <Box>
      <Flex
        justifyContent="space-between"
        alignItems="center"
        mb={6}
        flexWrap="wrap"
        gap={3}
      >
        <Heading size="lg">{t("app.notifications.title")}</Heading>
        <HStack gap={2} flexWrap="wrap">
          <AskStrikeUpButton
            context={t("app.askStrikeUp.context.notifications")}
          />
          <SetupGuideLink doc="notification-channels-setup" />
          <RefreshButton onClick={loadChannels} loading={loading} size="sm" />
          {missingProviders.map((provider) => (
            <Button
              key={provider}
              colorPalette="brand"
              size="sm"
              onClick={() => openAdd(provider)}
            >
              <MdAdd />
              {t("app.customerCompanies.integrations.addChannel", {
                provider: providerLabel(provider),
                defaultValue: "Add {{provider}}",
              })}
            </Button>
          ))}
        </HStack>
      </Flex>

      {error && (
        <Text color="red.400" mb={4}>
          {error}
        </Text>
      )}

      {/* Create form */}
      {editor && !editor.channel && (
        <Box mb={6}>
          <Card>{renderEditor(editor)}</Card>
        </Box>
      )}

      {/* Channel list */}
      {loading && !channels.length ? (
        <Text>{t("app.common.loading")}</Text>
      ) : channels.length === 0 ? (
        <Card>
          <Text color="gray.500" textAlign="center">
            {t("app.notifications.noChannels")}
          </Text>
        </Card>
      ) : (
        <VStack gap={4} alignItems="stretch">
          {channels.map((channel) => (
            <Card key={channel.id}>
              {editor?.channel?.id === channel.id ? (
                renderEditor(editor)
              ) : (
                <Flex
                  justifyContent="space-between"
                  alignItems="flex-start"
                  gap={3}
                  flexWrap="wrap"
                  opacity={channel.isActive ? 1 : 0.5}
                >
                  <Box>
                    <HStack gap={2} mb={1}>
                      <Text fontWeight="bold">{channel.name}</Text>
                      <Badge
                        colorPalette={STATUS_COLOR[channel.status] ?? "gray"}
                      >
                        {channel.status}
                      </Badge>
                      <Badge variant="outline">
                        {providerLabel(channel.providerType)}
                      </Badge>
                      {!channel.isActive && (
                        <Badge colorPalette="gray" variant="subtle">
                          {t("app.notifications.channel.disabled")}
                        </Badge>
                      )}
                    </HStack>
                    {channel.smtpSettings && (
                      <Text fontSize="sm" color="gray.500">
                        {channel.smtpSettings.fromEmail} ·{" "}
                        {channel.smtpSettings.host}:{channel.smtpSettings.port}
                        {channel.smtpSettings.enableSsl ? " (SSL)" : ""}
                      </Text>
                    )}
                    {channel.providerType === "Smtp" && (
                      <Text fontSize="sm" color="gray.500">
                        {recipientPolicySummary(channel, t)}
                      </Text>
                    )}
                    {channel.telegramSettings && (
                      <Text fontSize="sm" color="gray.500">
                        {channel.telegramSettings.botUsername
                          ? `@${channel.telegramSettings.botUsername} · `
                          : ""}
                        {tokenStatus(channel.telegramSettings.hasBotToken)}
                      </Text>
                    )}
                    {channel.slackSettings && (
                      <Text fontSize="sm" color="gray.500">
                        {slackChannelStatus(channel, t)}
                      </Text>
                    )}
                    {channel.providerType === "Teams" && (
                      <Text fontSize="sm" color="gray.500">
                        {teamsChannelStatus(channel, t)}
                      </Text>
                    )}
                    {testResults[channel.id] && (
                      <Text
                        fontSize="sm"
                        mt={1}
                        color={
                          testResults[channel.id].success
                            ? "green.400"
                            : "red.400"
                        }
                      >
                        {testResults[channel.id].success
                          ? t(
                              channel.providerType === "Smtp"
                                ? "app.notifications.channel.testSuccess"
                                : "app.notifications.channel.testSuccessBot",
                            )
                          : (testResults[channel.id].error ??
                            t("app.notifications.channel.testFailed"))}
                      </Text>
                    )}
                  </Box>
                  <HStack gap={2} flexShrink={0}>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleTest(channel.id)}
                      loading={testingId === channel.id}
                    >
                      <MdWifi />
                      {t("app.notifications.channel.test")}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEdit(channel)}
                    >
                      <MdEdit />
                      {t("app.common.edit")}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      colorPalette={channel.isActive ? "gray" : "green"}
                      onClick={() => handleToggleActive(channel.id)}
                      loading={togglingId === channel.id}
                    >
                      {channel.isActive ? <MdPause /> : <MdPlayArrow />}
                      {channel.isActive
                        ? t("app.notifications.channel.disable")
                        : t("app.notifications.channel.enable")}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      colorPalette="red"
                      onClick={() => handleDelete(channel.id)}
                      loading={deletingId === channel.id}
                    >
                      <MdDelete />
                      {t("app.common.delete")}
                    </Button>
                  </HStack>
                </Flex>
              )}
            </Card>
          ))}
        </VStack>
      )}
    </Box>
  );
};

export default Notifications;
